import { createSelector, createSelectorCreator, defaultMemoize, createStructuredSelector } from 'reselect';
import { getAccessToken } from '../AuthToken';
import { AGENDA_FILTERS } from '../constants/QueryReducer';
import { FILTERS_NAMES } from 'web-common/scripts/constants/Agenda';
import moment from 'moment';
import _ from 'underscore';
import config from 'config';
import { queryInstanceSelector } from './QuerySelectors';
import { siteConfSelector } from './SiteConfSelectors';
import { eventSelector } from './EventSelectors';
import { dateAndMinutesToTimestamp, DAY } from 'web-common/scripts/utils/DateHelper';
import {
    VIEW_MODE_SBS,
    VIEW_MODE_LIST,
    SESSIONS_TAB_ALL,
    SESSIONS_TAB_FAVOURITES,
    SESSIONS_TAB_REGISTRATIONS
} from '../constants/Enums';
import {rgbToAlfa} from './UtilSelectors';
import { userSelectedTicketsIdsSelector } from './UserProfilePageSelector';
import FeatureToggleUtils from 'FeatureToggleUtils';

export const REGISTERED = 'REGISTERED';
export const NOT_REGISTERED = 'NOT_REGISTERED';
export const DISABLED = 'DISABLED';
export const NONE = 'NONE';

const isLoadingSelector = state => state.agendaReducer.isLoading;

export const agendaSelector = ({ agendaReducer }) => agendaReducer;

export const activeTabSelector = ({ agendaReducer }) => agendaReducer.activeTab;

export const settingsSelector = ({ agendaReducer }) => agendaReducer.settings;

export const markersSelector = ({ agendaReducer }) => agendaReducer.markers;

const registeredSessionsByTicketId = state => !state.userProfilePageReducer.sessionRegisteredOn ? [] : state.userProfilePageReducer.sessionRegisteredOn;
const registeredSessionsPerUser = state => state.meReducer.registeredSessionIds === null ? [] : state.meReducer.registeredSessionIds;
export const ticketsRegisteredSessionIdsSelector = (state) => registeredSessionsByTicketId(state);

export const registeredSessionIdsSelector = (state) => {
    const registeredSessionIds = registeredSessionsByTicketId(state);
    return _.uniq(Object.values(registeredSessionIds).reduce((accumulator, value) => accumulator.concat(value), []));
};

export const sessionsOpenForRegistrationByTicketSelector = state => !state.userProfilePageReducer.sessionForRegistration ? [] : state.userProfilePageReducer.sessionForRegistration;

export const overlappingSessionsByTicketSelector = state => !state.userProfilePageReducer.overlappingSessions ? [] : state.userProfilePageReducer.overlappingSessions;

export const sessionsRegistrationTicketRulesSelector = state => !state.userProfilePageReducer.ticketRule ? null : state.userProfilePageReducer.ticketRule;

export const sessionsMap = (state) => state.agendaReducer.sessionsMap;

export const viewModeSelector = (state) => state.agendaReducer.viewMode;

export const favSessionIdsSelector = createSelector(
    markersSelector,
    state => state.meReducer.me,
    state => state.eventReducer.event,
    (markers, me, event) => _.where(markers, {
        entity: 'sessions',
        eventId: event.id,
        userProfileId: me.id,
        markerType: 'addToCal'
    }).map(marker => marker.entityId)
);

const sessionsByRegisteredSelector = createSelector(
    ({ agendaReducer }) => agendaReducer.sessions || { sessions: [], total: 0},
    ticketsRegisteredSessionIdsSelector,
    userSelectedTicketsIdsSelector,
    (sessions, registeredSessionIds, selectedTicketsIds) => {
        let mappedSessions = sessions || [];
        const isFilterVisibleSessionsByRegisteredEnabled = FeatureToggleUtils.isFeatureEnabled('filterVisibleSessionsByRegistered');
        if (isFilterVisibleSessionsByRegisteredEnabled){

            const selectedSessions = selectedTicketsIds.reduce((acc, current) => acc.concat(registeredSessionIds[current]), []);
            const sessionsFilteredByRegistered = mappedSessions.sessions.filter(session => {
                if (session.private){
                    return selectedSessions.includes(session.id);
                } else {
                    return true;
                }
            });
            mappedSessions = {
                ...sessions,
                sessions: sessionsFilteredByRegistered,
                total: sessionsFilteredByRegistered.length
            };
        }
        return mappedSessions;
    }
);

export const sessionsSelector = createSelector(
    sessionsByRegisteredSelector,
    favSessionIdsSelector,
    registeredSessionIdsSelector,
    activeTabSelector,
    (sessions, favIds, regIds, activeTab) => {
        let sessionIdsSource = [];
        switch (activeTab) {
        case SESSIONS_TAB_ALL:
            break;
        case SESSIONS_TAB_FAVOURITES:
            sessionIdsSource = favIds;
            break;
        case SESSIONS_TAB_REGISTRATIONS:
            sessionIdsSource = regIds;
            break;
        }
        sessions.ids = sessionIdsSource;
        return sessions;
    }
);

export const speakerWithFullNameSelector = ({ firstname = '', lastname = '', prefix = '', suffix = '' }) =>
    `${prefix} ${firstname} ${lastname} ${suffix}`.replace(/^\s+|\s+$/g, '').replace(/\s{2,}/g, ' ');

export const fillSpeakers = createSelector(
    ({ agendaReducer }) => agendaReducer.speakers || [],
    speakers => speakers.map(speaker => {
        speaker.fullName = speakerWithFullNameSelector(speaker);
        return speaker;
    })
);

export const speakersToObject = speakers => speakers.reduce(
    (speakers, speaker) => {
        speakers[speaker.id] = speaker;
        return speakers;
    }, {}
);

export const speakersSelector = createSelector(
    fillSpeakers,
    speakersToObject
);

export const speakersExistObj = createSelector(
    ({ agendaReducer }) => agendaReducer.speakers || [],
    speakers => speakers.reduce((acc, speaker) => ({...acc, [speaker.id]: speaker.id}), {})
);

export const markerSelector = createSelector(
    markers => markers,
    (_, props) => props,
    (markers, marker) => markers ? _.find(markers, marker) : null
);

export const shouldFetchMarkersSelector = createSelector(
    state => state.meReducer.me,
    markersSelector,
    (user, markers) => {
        const bizzaboToken = getAccessToken();
        const isUserLoggedIn = !!(user.id || bizzaboToken);
        return isUserLoggedIn && markers === null;
    }
);

export const shouldFetchAgendaSettingsSelector = createSelector(
    settingsSelector,
    settings => {
        if (settings === null) {
            return true;
        }
        return false;
    }
);

export const shouldFetchSpeakersSelector = createSelector(
    ({ agendaReducer }) => agendaReducer.speakers,
    (speakers) => !speakers
);

export const shouldFetchAgendaSelector = createSelector(
    shouldFetchAgendaSettingsSelector,
    ({ agendaReducer }) => agendaReducer.sessions,
    (shouldFetchAgendaSettings, sessions) => {
        if (shouldFetchAgendaSettings) {
            return true;
        }

        return !sessions;
    }
);

export const filterUISelector = state => state.agendaReducer.filterUI;

export const agendaDaysSelector = createSelector(
    state => state.agendaReducer.agendaDays,
    settingsSelector,
    (days, settings) => {
        if (settings === null) {
            return [];
        }
        return days.map(day => (
            {
                label: moment(day).utc().format(settings.dateFormat),
                value: day
            }
        ));
    }
);

const mapFilterTags = (tags = [], selectedFilters, filterCategoryName = '') => tags.map(tag => {
    const selectedEntry = _.find(selectedFilters, v => v.value === tag.id);
    const isSelected = Boolean(selectedEntry);
    return {
        ...tag,
        isSelected,
        categoryName: filterCategoryName
    };
}
);

const mapLocationFilterTags = (tags = [], queryInstance) => {
    const selectedLocations = queryInstance.filter(v => v.key === 'locations');
    return tags.map(
        tag => {
            const selectedEntry = _.find(selectedLocations, v => v.value === tag.id);
            const isSelected = Boolean(selectedEntry);
            const selectedSublocations = queryInstance.filter(v => v.key === 'sublocations');
            return {
                ..._.omit(tag, 'sublocations'),
                subTags: mapFilterTags(tag.sublocations, selectedSublocations, 'sublocations'),
                isSelected,
                categoryName: 'locations'
            };
        }
    );
};

const mapFilters = (parsingSettings) => {
    const { filters = [], queryInstance } = parsingSettings;
    const selectedFilters = _.reject(queryInstance, v => FILTERS_NAMES.includes(v.key));

    return filters.map(filter => ({
        ...filter,
        id: parsingSettings.tagsCategoryId++,
        tags: mapFilterTags(filter.tags, selectedFilters, filter.name)
    }));
};

const getSessionsValuesIfSingleTicket = (selectedTicketsIds, ticketsSessions) =>{
    if (!selectedTicketsIds || selectedTicketsIds.length > 1){
        return [];
    }
    return getSessionsValuesOfTicket(selectedTicketsIds[0], ticketsSessions);
};

export const getSessionsValuesOfTicket = (ticketId, ticketsSessions) =>{
    if (!ticketsSessions || !Object.keys(ticketsSessions).length){
        return [];
    }
    return ticketsSessions[ticketId] || [];
};

const isSessionFoundInTickets = (ticketsSessionIds, sessionId) => _.flatten(Object.values(ticketsSessionIds || {})).includes(sessionId);

// TODO refactor / simplify
export const fillSessionWithExternalData = (markers, me, ticketsRegisteredSessionIds, ticketsOpenRegSessionsIds, selectedTicketsIds, ticketsRules, ticketsOverlappingSessions, event, settings, session, sponsors) => {
    const addToCalMarker = {
        entity: 'sessions',
        entityId: session.id,
        eventId: event.id,
        userProfileId: me.id,
        markerType: 'addToCal'
    };
    const isLoggedIn = !!me.id;
    const favMarker = isLoggedIn ? markerSelector(markers, addToCalMarker) : null;
    const isInFavourite = !!(isLoggedIn && favMarker);
    const isOpenForRegistration = isSessionFoundInTickets(ticketsOpenRegSessionsIds, session.id);
    const isRegistered = isSessionFoundInTickets(ticketsRegisteredSessionIds, session.id);
    const singleTicketRegisteredSessionIds = getSessionsValuesIfSingleTicket(selectedTicketsIds, ticketsRegisteredSessionIds);
    const singleTicketOverlappingSessions = getSessionsValuesIfSingleTicket(selectedTicketsIds, ticketsOverlappingSessions);
    const singleTicketRules = getSessionsValuesIfSingleTicket(selectedTicketsIds, ticketsRules);
    const disableType = getDisableType(session, isRegistered, singleTicketRegisteredSessionIds, singleTicketRules, singleTicketOverlappingSessions);
    const location = session.locationId ?
        settings.locations.find(location => session.locationId === location.id) ||
        null :
        null;
    const sublocation = (location && session.locationId && session.sublocationId && location.sublocations) ?
        location.sublocations.find(sublocation => session.sublocationId === sublocation.id) ||
        null :
        null;
    const sessionTags = session.filters ? session.filters.reduce((sessionTags, filter) => {
        const category = _.find(settings.filters, { id: filter.id });
        if (category && category.tags) {
            filter.tags.forEach((tag) => {
                const sessionTag = _.find(category.tags, { id: tag });
                sessionTag && sessionTags.push(sessionTag.name);
            });
        }
        return sessionTags;
    }, []) : null;
    let sessionSponsors = [];
    if (session.sponsors && session.sponsors.length) {
        sessionSponsors = session.sponsors.reduce((acc, sponsorId) => {
            const sponsor = sponsors.find(sponsor => sponsor.id === sponsorId);
            return sponsor ? [...acc, {...sponsor}] : acc;
        }, []);
    }

    return {
        ...session,
        addToCalMarker,
        isInFavourite,
        location,
        sublocation,
        sessionTags,
        ticketsRegisteredSessionIds,
        isRegistered,
        disableType,
        ticketsOpenRegSessionsIds,
        isOpenForRegistration,
        sponsors: [
            ...sessionSponsors
        ],
        ticketsOverlappingSessions,
        ticketsRules
    };
};

export const getDisableType = (session, isRegistered, registeredSessionIds, ticketRules, overlappingSessions) => {
    let disableType = '';

    if (!isRegistered) {
        if (ticketRules && registeredSessionIds && ticketRules.max <= registeredSessionIds.length) {
            disableType = 'maxLimitReached';
        } else if (session.registrationFull) {
            disableType = 'registrationFull';
        } else if (session.overlapping || overlappingSessions && overlappingSessions.includes(session.id)) {
            disableType = 'overlapping';
        }
    }

    return disableType;
};

const buildSessionsByDay = (markers, activeTabSelector, me, registeredSessionIds, openSessRegSessionsIds, ticketRules, overlappingSessions, event, settings, sessions, sponsors, selectedTicketsIds ) => {
    if (settings === null) {
        return [];
    }
    return {
        ...sessions,
        sessions: sessions.sessions
            .filter(session => session)
            .map(session => fillSessionWithExternalData(markers, me, registeredSessionIds, openSessRegSessionsIds, selectedTicketsIds, ticketRules, overlappingSessions, event, settings, session, sponsors))
    };
};

export const sponsorsSelector = createSelector(
    ({ sponsorsReducer }) => sponsorsReducer['event-partners'].sponsors,
    sponsors => sponsors
);

export const sessionsByDaySelector = createSelector(
    markersSelector,
    activeTabSelector,
    state => state.meReducer.me,
    ticketsRegisteredSessionIdsSelector,
    sessionsOpenForRegistrationByTicketSelector,
    sessionsRegistrationTicketRulesSelector,
    overlappingSessionsByTicketSelector,
    state => state.eventReducer.event,
    settingsSelector,
    sessionsSelector,
    sponsorsSelector,
    state => state.userProfilePageReducer.selectedTicketsIds,
    buildSessionsByDay
);

export const selectedDaysSelector = createSelector(
    state => (queryInstanceSelector(state, { name: AGENDA_FILTERS }) || []).filter(v => v.key === 'dates'),
    state => state.agendaReducer.agendaDays,
    (selectedDays, agendaDays) => selectedDays.length === 0 ? agendaDays : _.pluck(selectedDays, 'value')
);

export const timeSelector = createSelector(
    state => queryInstanceSelector(state, { name: AGENDA_FILTERS }),
    (_, props) => props.timeKey,
    (queryInstance, timeKey, ) => {
        const time = _.find(queryInstance, r => r.key === timeKey);
        const defaultTime = timeKey === 'startTime' ? 0 : 24;
        if (!time) {
            return defaultTime;
        }
        if (time.value === 0) {
            return 0;
        }
        return time.value / 60;
    }
);

export const parse24HourTimeInto12Hour = time => {
    if (time === 0 || time === 24) {
        return '12:00am';
    }
    if (time === 12) {
        return '12:00pm';
    }
    return time < 13 ? `${time}:00am` : `${time - 12}:00pm`;
};

export const agendaFilterSelector = createSelector(
    settingsSelector,
    siteConfSelector,
    sessionsSelector,
    filterUISelector,
    state => queryInstanceSelector(state, { name: AGENDA_FILTERS }),
    state => timeSelector(state, { timeKey: 'startTime' }),
    state => timeSelector(state, { timeKey: 'endTime' }),
    (settings, siteConf, sessions, filterUI, queryInstance = [], startTime, endTime) => {
        if (settings === null) {
            return null;
        }
        const { filters, locations } = settings;
        const { total } = sessions;
        const parsingSettings = {
            filters,
            queryInstance,
            tagsCategoryId: 1
        };
        return {
            label: 'Sessions',
            counter: total,
            filterUI,
            filters: [
                {
                    type: 'TIME',
                    id: parsingSettings.tagsCategoryId++,
                    startTime,
                    startTimeLabel: parse24HourTimeInto12Hour(startTime),
                    endTime,
                    endTimeLabel: parse24HourTimeInto12Hour(endTime)
                },
                {
                    type: 'TAGS',
                    filters: mapFilters(parsingSettings)
                },
                {
                    type: 'NESTED_TAGS',
                    filters: locations.length === 0 ? [] : [
                        {
                            id: parsingSettings.tagsCategoryId++,
                            name: 'Locations',
                            tags: mapLocationFilterTags(locations, queryInstance)
                        }
                    ]
                }
            ]
        };
    });

export const getQueryParamName = (FILTERS_NAMES, instanceKey) => FILTERS_NAMES.includes(instanceKey) ?
    instanceKey : 'filters';

export const sessionsAggregationsSelector = createSelector(
    sessionsSelector,
    sessions => {
        const { filterTagAggregation, locationsAggregations, sublocationsAggregations } = sessions;
        const filters = filterTagAggregation || [];
        const locations = locationsAggregations || [];
        const sublocations = sublocationsAggregations || [];

        return {
            filters: filters.map(f => ({ id: f.tagId, count: f.count })),
            locations: locations.map(f => ({ id: f.locationId, count: f.count })),
            sublocations: sublocations.map(f => ({ id: f.sublocationId, count: f.count }))
        };
    }
);

const parseSelectedTimeTag = timeFilter => ({
    name: `${timeFilter.startTimeLabel} - ${timeFilter.endTimeLabel}`
});

const injectAggregation = (aggregatedTags, tag) => {
    const aggregatedTag = _.find(aggregatedTags, aggTag => aggTag.id === tag.id);
    return aggregatedTag ? { ...tag, ...aggregatedTag } : { ...tag, count: 0 };
};

const extractTags = (agendaFilter) => {
    const tagsFilter = _.find(agendaFilter.filters, filter => filter.type === 'TAGS').filters;
    const filtersTags = tagsFilter ? _.flatten(_.pluck(tagsFilter, 'tags')) : [];
    return filtersTags;
};

const extractLocationsTags = (agendaFilter) => {
    const locationsFilter = _.find(
        _.find(agendaFilter.filters, filter => filter.type === 'NESTED_TAGS').filters,
        f => f.name === 'Locations'
    );

    const locationsTags = locationsFilter ? locationsFilter.tags : [];

    const sublocationsTags = _.flatten(_.pluck(locationsTags, 'subTags'));

    return {
        locationsTags,
        sublocationsTags
    };
};

const extractSublocation = (locationsTags, sublocationsTags, entry) => {
    const sublocation = _.find(sublocationsTags, tag => tag.id === parseInt(entry.value));
    const location = _.find(locationsTags, tag => tag.id === sublocation.locationId);
    const name = `${location.name} - ${sublocation.name}`;
    return {
        ...sublocation,
        name
    };
};

const extractSelectedTags = (
    agendaQueryInstance,
    aggregations,
    filtersTags,
    locationsTags,
    sublocationsTags,
    speakersAsObject
) => agendaQueryInstance ?
    agendaQueryInstance.reduce((selectedTags, entry) => {
        const queryParamName = getQueryParamName(FILTERS_NAMES, entry.key);
        switch (queryParamName) {
        case 'filters': {
            const tag = filtersTags.find(tag => tag.id === parseInt(entry.value));
            return tag ? [
                ...selectedTags,
                injectAggregation(aggregations[queryParamName], tag)
            ] : selectedTags;
        }
        case 'locations': {
            const tag = _.omit(_.find(locationsTags, tag => tag.id === parseInt(entry.value)), 'subTags');
            return [
                ...selectedTags,
                injectAggregation(aggregations[queryParamName], tag)
            ];
        }
        case 'sublocations': {
            const sublocation = extractSublocation(locationsTags, sublocationsTags, entry);
            return [
                ...selectedTags,
                injectAggregation(aggregations[queryParamName], sublocation)
            ];
        }
        case 'speakers': {
            const speaker = speakersAsObject[entry.value];
            if (speaker) {
                return [
                    ...selectedTags,
                    { ...speaker, name: speaker.fullName, categoryName: 'speakers' }
                ];
            }
            break;
        }
        case 'sessionsIds':
            return selectedTags;
        case 'privateFilters':
            // Private filters shouldn't show up in the selected tags UI
            return selectedTags;
        }
        return selectedTags;
    }, []) :
    [];

export const selectedTagsSelector = createSelector(
    state => queryInstanceSelector(state, { name: AGENDA_FILTERS }),
    agendaFilterSelector,
    sessionsAggregationsSelector,
    speakersSelector,
    (agendaQueryInstance, agendaFilter, aggregations, speakersAsObject) => {
        if (!agendaQueryInstance) {
            return [];
        }
        const filtersTags = extractTags(agendaFilter);
        const { locationsTags, sublocationsTags } = extractLocationsTags(agendaFilter);

        const timeFilter = agendaFilter.filters[0];
        const { startTime, endTime } = timeFilter;

        const selectedTags = extractSelectedTags(
            agendaQueryInstance,
            aggregations,
            filtersTags,
            locationsTags,
            sublocationsTags,
            speakersAsObject
        );

        return startTime === 0 && endTime === 24 ?
            selectedTags :
            [...selectedTags, parseSelectedTimeTag(timeFilter)];
    }
);

export const hasSessionsSelector = createSelector(
    state => queryInstanceSelector(state, { name: AGENDA_FILTERS }),
    sessionsSelector,
    isLoadingSelector,
    (queryInstance = [], sessions, isLoading) => {
        const hasQueryParams = queryInstance.length !== 0;
        const hasSessions = sessions.sessions.length !== 0;

        return isLoading ||
            hasQueryParams ||
            hasSessions;
    }
);

export const agendaSpeakersSelector = ({ agendaReducer }) => agendaReducer.speakers || [];


export const agendaSpeakerSelector = createSelector(
    [
        state => state.agendaReducer.speakers,
        (_, props) => props
    ],
    (speakers, { eventId, speakerId }) => {
        if (speakers === null) {
            return null;
        }
        return _.find(speakers, { eventId, id: parseInt(speakerId) });
    }
);

export const agendaSessionSelector = createSelector(
    sessionsSelector,
    (_, { eventId, sessionId }) => ({ eventId, sessionId }),
    (sessions, { eventId, sessionId }) => {
        const session = _.find(
            sessions.sessions,
            (session) => eventId === session.eventId && sessionId === session.id
        );
        return session || null;
    }
);


export const agendaSpeakersByIds = createSelector(
    [
        speakersSelector,
        (_, { ids }) => ids
    ],
    (speakers, ids) => {
        const sessionSpeakers = ids.reduce((sessionSpeakers, id) => {
            const speaker = speakers[id];
            return speaker ? [...sessionSpeakers, speaker]: sessionSpeakers;
        }, []);
        return sessionSpeakers.length ? sessionSpeakers : null;
    }
);

export const shouldFetchRegistrationEnabledSelector = createSelector(
    [agendaSelector],
    ({ isSessionRegistrationEnabled }) => isSessionRegistrationEnabled === null
);

export const windowInnerWidthSelector = () => window.innerWidth;

const MIN_WINDOW_WIDTH_FOR_SESSION_PREVIEW = 1000;
const MIN_WINDOW_WIDTH_FOR_STICKY_AGENDA_SEARCH = 1000;

export const shouldShowSessionPreviewWindowSelector = createSelector(
    [eventSelector,
        settingsSelector,
        windowInnerWidthSelector],
    (event, settings, windowWidth) => {
        const { websiteType } = event;
        const rule = websiteType !== 'independent' && settings && settings.sessionPreviewWindow && windowWidth > MIN_WINDOW_WIDTH_FOR_SESSION_PREVIEW;
        return rule || false;
    }
);

export const shouldStickAgendaSearchSelector = createSelector(
    [eventSelector,
        windowInnerWidthSelector],
    (event, windowWidth) => {
        const { websiteType } = event;
        const rule = websiteType !== 'independent' && windowWidth > MIN_WINDOW_WIDTH_FOR_STICKY_AGENDA_SEARCH;
        return rule || false;
    }
);

const getSessRegStatus = (registered, open, disableType, showDefault, hideButton) => {
    if (hideButton) return NONE;
    if (showDefault) return NOT_REGISTERED;
    if (disableType) return DISABLED;
    if (registered) return REGISTERED;
    if (open && !registered) return NOT_REGISTERED;
    return NOT_REGISTERED;
};

export const getSessionRegButtonStatus = (session, isAuthorized, tickets, selectedTicketsIds) => {
    const isTicketSelectedOrDefault = !!selectedTicketsIds.length || tickets != null && tickets.length === 1;
    const shouldHideRegButton = !isAuthorized || isAuthorized && !isTicketSelectedOrDefault;
    const shouldRenderSessRegButton = !isTicketSelectedOrDefault ? session?.registration && !shouldHideRegButton : session?.isOpenForRegistration && !shouldHideRegButton;
    const sessRegStatus = getSessRegStatus(session?.isRegistered, session?.isOpenForRegistration, session?.disableType, !isAuthorized || !isTicketSelectedOrDefault, shouldHideRegButton);

    return { shouldRenderSessRegButton, sessRegStatus };
};

export const shouldShowSessionDescriptionAgenda = createSelector(
    settingsSelector,
    settings => settings && !!settings.sessionDescription || false
);

export const getTimesAndDatesBySession = (sessions = [], prevDate = {}) => sessions.reduce((dates, session) => {
    if (session && session.startDate && session.endDate) {
        if ( dates[session.startDate] === undefined) {
            dates[session.startDate] = ({ first: session.id });
        }
        if (dates[session.startDate][session.startMinute] === undefined) {
            dates[session.startDate][session.startMinute] = session.id;
        }
    } else if (session && dates.unscheduled === undefined) {
        dates.unscheduled = session.id;
    }
    return dates;
}, prevDate);

export const prepareEmptySBSMapObject = sessionsMap => Object.keys(sessionsMap).reduce((acc, sessionId) => {
    const sessionMapObj = sessionsMap[sessionId];
    const dateKey = sessionMapObj.startDate && sessionMapObj.startMinute >= 0 ? dateAndMinutesToTimestamp(sessionMapObj.startDate, sessionMapObj.startMinute): 'undefined';
    const isDateAlreadyExist = acc[dateKey];
    if (!isDateAlreadyExist) {
        acc[dateKey] = {
            first: undefined,
            excluded: [],
            slider: [],
            last: undefined,
        };
    }
    return acc;
}, {});

const getIntersectionIds = ( firstArr, secondArr ) =>_.intersection(firstArr, secondArr);

export const sessionsMapSelector = createSelector(
    [
        sessionsMap,
        sessionsSelector,
        activeTabSelector
    ],
    ( sessionsMap, { sessions, ids }, activeTab ) => {
        const SBSSessionsMap = prepareEmptySBSMapObject(sessionsMap);
        const listSessionsMap = getTimesAndDatesBySession(sessions);
        Object.keys(sessionsMap).map((sessionId) => {
            const numSessionId = parseInt(sessionId);
            const { startDate, startMinute, title } = sessionsMap[numSessionId];
            const timestamp = startDate && startMinute >= 0 ? dateAndMinutesToTimestamp(startDate, startMinute): 'undefined';
            const session = getSessionById(sessions, numSessionId);

            const SESSION_NOT_LOADED_AND_SCHEDULED = !session && timestamp !== 'undefined';
            const SESSION_NOT_LOADED_AND_UNSCHEDULED = !session && timestamp === 'undefined';
            const SESSION_LOADED = !!session;
            const SESSION_SHOULD_BE_IN_SLIDER = session && !session.excluded && timestamp !== 'undefined';
            const SESSION_SHOULD_BE_EXCLUDED = session && session.excluded || timestamp === 'undefined';

            if (SESSION_NOT_LOADED_AND_SCHEDULED) {
                return SBSSessionsMap[timestamp].slider.push({id: numSessionId, title: title});
            }
            if (SESSION_NOT_LOADED_AND_UNSCHEDULED) {
                return SBSSessionsMap[timestamp].excluded.push({id: numSessionId, title: title});
            }
            if (SESSION_LOADED && SESSION_SHOULD_BE_IN_SLIDER) {
                return SBSSessionsMap[timestamp].slider.push({id: numSessionId, title: session.title});
            }
            if (SESSION_LOADED && SESSION_SHOULD_BE_EXCLUDED) {
                return SBSSessionsMap[timestamp].excluded.push({id: numSessionId, title: session.title});
            }
        });
        _.map(SBSSessionsMap, timestampObj => {
            if (timestampObj.slider.length > 0) {
                timestampObj.slider.sort((a, b) => a.title.localeCompare(b.title, undefined, {numeric: true}));
                timestampObj.slider = _.map(timestampObj.slider, 'id');
            } else if (timestampObj.excluded.length > 0) {
                timestampObj.excluded.sort((a, b) => a.title.localeCompare(b.title, undefined, {numeric: true}));
                timestampObj.excluded = _.map(timestampObj.excluded, 'id');
            }

            const filteredExcluded = activeTab === SESSIONS_TAB_ALL ? timestampObj.excluded: getIntersectionIds(timestampObj.excluded, ids);
            const filteredSlider = activeTab === SESSIONS_TAB_ALL ? timestampObj.slider: getIntersectionIds(timestampObj.slider, ids);
            timestampObj.first = timestampObj.excluded.length ? filteredExcluded[0] : filteredSlider[0];
        });
        return {
            SBSSessionsMap,
            listSessionsMap
        };
    }
);

export const getVisibleSlidesCount = (ids) => {
    if (!ids) {
        return 1;
    }
    const length = ids.length;
    if (length === 0 || length === 1) {
        return 1;
    }
    if (length === 2) {
        return 2;
    }
    if (length >= 3) {
        return 3;
    }
};

export const checkIfItIsFirstDay = ({ id, startDate, timestamp }, sessionsMap) => {
    if (startDate === timestamp) {
        return true;
    }

    if (timestamp === 'undefined') {
        return sessionsMap['undefined'].first === id;
    }
    const nextDay = startDate + DAY;
    const timeUnits = [];

    Object.keys(sessionsMap).forEach(day => {
        if (!isNaN(parseInt(day)) ) {
            timeUnits.push(parseInt(day));
        }
    });

    const timeUnitsByDay = timeUnits.filter(timeUnit => timeUnit >= startDate && timeUnit < nextDay);
    timeUnitsByDay.sort((a, b) => a -b);
    return timeUnitsByDay[0] === timestamp;
};

const shouldRenderElements = {
    renderSessionInfo: true,
    renderBroadcast: true,
    renderSessionDescription: false,
    renderSponsors: false,
    renderSpeakers: true,
};

const checkRenderRules = (session, shouldRenderElements) => {
    if (session.description ) {
        shouldRenderElements.renderSessionDescription = true;
    }
    if (session.sponsors && session.sponsors.length) {
        shouldRenderElements.renderSponsors = true;
    }
    if (session.speakers ) {
        shouldRenderElements.renderSpeakers = true;
    }
};

const fillSessionWithRenderData = (session, sessionsMap, sessions, activeTab, ids) => {
    const { id, startDate, startMinute } = session;
    session.timestamp = startDate && startMinute >= 0 ? dateAndMinutesToTimestamp(startDate, startMinute): 'undefined';
    const timestampObj = sessionsMap[session.timestamp];
    const isValidDateTime = startDate && startMinute >= 0;
    session.isOneLineElement = timestampObj.slider.includes(id);
    session.shouldShowDate = checkIfItIsFirstDay(session, sessionsMap);
    session.firstUnscheduledSession = sessionsMap['undefined'] && sessionsMap['undefined'].first === session.id;
    session.showTime = timestampObj.first === id && isValidDateTime;


    session.slides = [];
    session.missedSessions = [];
    session.slidesToShow = 3;

    if (session.isOneLineElement) {
        const idsToRender = activeTab !== SESSIONS_TAB_ALL ? _.intersection(timestampObj.slider, ids) : timestampObj.slider;
        session.elementsToRender = {...shouldRenderElements};
        checkRenderRules(session, session.elementsToRender);
        session.slidesToShow = idsToRender.filter(id => sessions.find(session => id === session.id));
        session.slidesToShow = getVisibleSlidesCount(session.slidesToShow);
        idsToRender.map(sessionId => {
            const sliderSession = getSessionById(sessions, sessionId);
            if (sliderSession) {
                checkRenderRules(sliderSession, session.elementsToRender);
            }
            session.slides.push( sliderSession || session);
            if (!sliderSession) {
                session.missedSessions.push(sessionId);
            }
        });

    }
    return session;
};

export const updateSessionSpeakers = (sessions, speakersExistObj) =>
    sessions.map(session =>
        session.speakers
            ? ({
                ...session,
                speakers: session.speakers = session.speakers
                    .reduce((acc, speaker) => speakersExistObj[speaker.speakerId]
                        ? [...acc, speaker]
                        : acc, [])
            })
            : session
    );

export const sessionsToRenderSelector = createSelector(
    [
        sessionsByDaySelector,
        sessionsMapSelector,
        viewModeSelector,
        activeTabSelector,
        speakersExistObj
    ],
    ( { sessions, ids }, sessionsMap, viewMode, activeTab, speakerExist) => {
        if (Object.keys(speakerExist).length) {
            sessions = updateSessionSpeakers(sessions, speakerExist);
        }
        if (viewMode === VIEW_MODE_LIST) {
            return activeTab === SESSIONS_TAB_ALL? sessions: sessions.filter(session => ids.includes(session.id));
        }
        if (viewMode === VIEW_MODE_SBS) {
            return _.reduce(sessionsMap.SBSSessionsMap, (sessionsToRender, timestampObj) => {
                timestampObj.excluded.map((sessionId) => {
                    const session = sessions.find(session => session.id === sessionId);
                    if (session) {
                        sessionsToRender.push(fillSessionWithRenderData(session, sessionsMap.SBSSessionsMap, sessions, activeTab, ids));
                    }
                });
                timestampObj.slider.find((sessionId) => {
                    const session = sessions.find(session => session.id === sessionId);
                    if (session) {
                        sessionsToRender.push(fillSessionWithRenderData(session, sessionsMap.SBSSessionsMap, sessions, activeTab, ids));
                        return true;
                    }
                });
                sessionsToRender.sort((currSession, nextSession) => {
                    if (currSession.timestamp !== 'undefined' && nextSession.timestamp === 'undefined') {
                        return -1;
                    } else if (currSession.timestamp === 'undefined' && nextSession.timestamp !== 'undefined') {
                        return 1;
                    } else if (currSession.timestamp === 'undefined' && nextSession.timestamp === 'undefined') {
                        return currSession.title.localeCompare(nextSession.title, undefined, {numeric: true});
                    }

                    return currSession.timestamp - nextSession.timestamp;
                });
                return activeTab === SESSIONS_TAB_ALL ? sessionsToRender: sessionsToRender.filter(session => ids.includes(session.id));

            }, []);
        }
        return sessions;
    }
);

export const getSessionById = (sessions, id) => sessions.find(session => session.id === id);

export const ticketsSelectionDataSelector = createStructuredSelector({
    tickets: createSelector(
        state => state.userProfilePageReducer.tickets,
        tickets => tickets
    ),
    me: createSelector(
        state => state.meReducer.me,
        ({ firstName, lastName, primaryEmail }) => ({ firstName, lastName, primaryEmail })
    ),
    selectedTicketsIds: createSelector(
        state => state.userProfilePageReducer.selectedTicketsIds,
        selectedTicketsIds => selectedTicketsIds
    ),
    ticketSelectionOpen: createSelector(
        state => state.userProfilePageReducer.ticketSelectionOpen,
        ticketSelectionOpen => ticketSelectionOpen
    ),
    event: createSelector(
        state => state.eventReducer.event,
        ({ name, id }) => ({ name, eventId: id })
    ),
    registrationsUrl: createSelector(
        state => state.eventReducer.event,
        event => event.registrationsUrl ? event.registrationsUrl : event['homepage-url']
    ),
    color1: createSelector(
        siteConfSelector,
        siteConf => siteConf.color1
    )
});

export const isSaveASeatVisible = (shouldRenderSessRegButton, sessionView, isAuthorized, isTicketSelectedOrDefault) =>
    shouldRenderSessRegButton && (!sessionView || (isAuthorized && isTicketSelectedOrDefault));

export const ticketsRegistrationSelector = createSelector(
    eventSelector,
    state => state.userProfilePageReducer.tickets,
    registeredSessionsByTicketId,
    sessionsOpenForRegistrationByTicketSelector,
    overlappingSessionsByTicketSelector,
    sessionsRegistrationTicketRulesSelector,
    state => state.userProfilePageReducer.selectedTicketsIds,
    siteConfSelector,
    ({websiteType}, tickets, ticketsRegisteredSessionIds, ticketsOpenRegSessionsIds, ticketsOverlappingSessions,
        ticketsRules, selectedTicketsIds, {color1}) => tickets
        .filter(({id}) => selectedTicketsIds && selectedTicketsIds.includes(id))
        .map(({id, ticketName}) => {
            const backgroundColor = color1 && `var(--page-dropdown-color-3, ${rgbToAlfa(color1, 0.15)}`;
            const ticketsRegisteredSession = getSessionsValuesOfTicket(id, ticketsRegisteredSessionIds);
            const ticketOverlappingSessions = getSessionsValuesOfTicket(id, ticketsOverlappingSessions);
            const ticketRules = getSessionsValuesOfTicket(id, ticketsRules);
            const ticketOpenForRegistration = getSessionsValuesOfTicket(id, ticketsOpenRegSessionsIds);
            return {
                id,
                ticketName,
                websiteType,
                backgroundColor,
                ticketsRegisteredSession,
                ticketOverlappingSessions,
                ticketRules,
                ticketOpenForRegistration
            };
        }));
